export class HHTabsAccordions extends HTMLElement {
  constructor() {
    super();
    this.breakpoint = 750;
    this.mode = 'accordion';
    this.accordions = [];
    this.tabButtons = [];
    this.tabList = null;
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.accordions = Array.from(this.querySelectorAll('hh-accordion'));
      if (this.accordions.length <= 1) return;

      this.setupMode();
      this.openFirstAccordion();
      window.addEventListener('resize', () => this.setupMode());
    });
  }

  setupMode() {
    const isDesktop = window.innerWidth >= this.breakpoint;
    const newMode = isDesktop ? 'tabs' : 'accordion';
    if (newMode === this.mode) return;

    this.mode = newMode;
    this.mode === 'tabs' ? this.enableTabs() : this.disableTabs();
    if (newMode === 'accordion') this.openFirstAccordion();
  }

  enableTabs() {
    this.createTabList();
    this.populateTabList();
    this.insertBefore(this.tabList, this.firstChild);
    this.classList.add('tabs-mode');
  }

  disableTabs() {
    if (!this.tabList) return;

    this.accordions.forEach((accordion, index) => {
      const btn = this.tabButtons[index];
      const panel = accordion.querySelector('.accordion-content');
      if (!btn || !panel) return;

      accordion.insertBefore(btn, accordion.firstChild);
      this.clearButtonAttributes(btn);
      this.clearPanelAttributes(panel);
      panel.hidden = false;
    });

    this.tabList.remove();
    this.tabList = null;
    this.tabButtons = [];
    this.classList.remove('tabs-mode');
  }

  createTabList() {
    this.tabList = document.createElement('div');
    this.tabList.setAttribute('role', 'tablist');
    this.tabList.className = 'hh-tab-list';
    this.tabButtons = [];
  }

  populateTabList() {
    this.accordions.forEach((accordion, index) => {
      const btn = accordion.querySelector('.accordion-summary');
      const panel = accordion.querySelector('.accordion-content');
      if (!btn || !panel) return;

      const tabId = `tab-${index}`;
      const panelId = `panel-${index}`;

      this.setButtonAttributes(btn, tabId, panelId, index === 0);
      this.setPanelAttributes(panel, panelId, tabId, index === 0);

      btn.dataset.index = index;
      btn.addEventListener('click', () => this.activateTab(index));
      btn.addEventListener('keydown', (e) => this.onKeydown(e));

      this.tabButtons[index] = btn;
      this.tabList.appendChild(btn);
    });
  }

  setButtonAttributes(btn, tabId, panelId, isSelected) {
    btn.setAttribute('role', 'tab');
    btn.setAttribute('id', tabId);
    btn.setAttribute('aria-controls', panelId);
    btn.setAttribute('tabindex', isSelected ? '0' : '-1');
    btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
  }

  setPanelAttributes(panel, panelId, tabId, isVisible) {
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('id', panelId);
    panel.setAttribute('aria-labelledby', tabId);
    panel.hidden = !isVisible;
  }

  clearButtonAttributes(btn) {
    btn.removeAttribute('role');
    btn.removeAttribute('id');
    btn.removeAttribute('aria-controls');
    btn.removeAttribute('tabindex');
    btn.removeAttribute('aria-selected');
    btn.removeEventListener('keydown', this.onKeydown);
  }

  clearPanelAttributes(panel) {
    panel.removeAttribute('role');
    panel.removeAttribute('id');
    panel.removeAttribute('aria-labelledby');
  }

  activateTab(index) {
    this.tabButtons.forEach((btn, i) => {
      const panel = this.accordions[i].querySelector('.accordion-content');
      const selected = i === index;
      btn.setAttribute('aria-selected', selected);
      btn.setAttribute('tabindex', selected ? '0' : '-1');
      panel.hidden = !selected;
    });

    this.tabButtons[index].focus();
  }

  openFirstAccordion() {
    if (window.innerWidth < this.breakpoint && this.accordions.length > 0) {
      const firstAccordion = this.accordions[0];
      const shadow = firstAccordion.shadowRoot;
      if (!shadow) return;
  
      const button = shadow.querySelector('slot')
        ?.assignedElements()
        ?.find(el => el.tagName === 'BUTTON');
      const content = shadow.querySelector('slot')
        ?.assignedElements()
        ?.find(el => el.tagName !== 'BUTTON');
  
      if (button && content) {
        button.classList.add('active');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    }
  }

  onKeydown(e) {
    const current = parseInt(e.currentTarget.dataset.index);
    let next = current;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % this.tabButtons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + this.tabButtons.length) % this.tabButtons.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabButtons.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.activateTab(next);
  }
}

customElements.define('hh-tabs-accordions', HHTabsAccordions);
